import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../../hooks/useSubscription';
import { SubscriptionPlan } from '@domain/subscription/models/SubscriptionPlan';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { SubscriptionCard } from './SubscriptionCard';
import { SelectShopifyShopDialog } from './SelectShopifyShopDialog';
import { useStripeCheckout } from '../../../hooks/useStripeCheckout';
import { useProducts } from '@features/products';
import { container } from '@infrastructure/container';
import { useConnections } from '@shared/hooks';
import { ClientType } from '@domain/connection/models/Connector';

interface UpdateSubscriptionDialogProps {
  open: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

export const UpdateSubscriptionDialog: React.FC<UpdateSubscriptionDialogProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const {
    plans,
    currentSubscription,
    isLoadingPlans,
    plansError,
  } = useSubscription();

  const { redirectToCheckout } = useStripeCheckout();

  const { products } = useProducts();
  const { connections } = useConnections();

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null);
  const [shopCheckoutError, setShopCheckoutError] = useState<string | null>(null);

  const connectionsLoaded = connections !== undefined;
  const shopifyConnections = useMemo(
    () => connections?.filter(c => c.connector.type === ClientType.SHOPIFY) ?? [],
    [connections],
  );
  const authorizedShops = useMemo(
    () => shopifyConnections.filter(shop => shop.authorized),
    [shopifyConnections],
  );
  const soleAuthorizedShop = authorizedShops.length === 1 ? authorizedShops[0] : undefined;
  const shopDialogOpen = Boolean(open && pendingPlan);
  const planDialogOpen = open;

  useEffect(() => {
    if (!open) return;
    setSelectError(null);
    setPendingPlan(null);
    setShopCheckoutError(null);
    setIsSelecting(false);
  }, [open]);

  const startShopifyCheckout = async (plan: SubscriptionPlan, connectionId?: string) => {
    const result = await container.subscriptionRepository.startShopifyCheckout(plan.id, connectionId);
    if (result.status === 'success' && result.data?.confirmationUrl) {
      window.location.href = result.data.confirmationUrl;
      return;
    }
    const message = result.status === 'error'
      ? result.error?.message ?? 'Could not start Shopify checkout'
      : 'Could not start Shopify checkout';
    setShopCheckoutError(message);
    setPendingPlan(plan);
    setIsSelecting(false);
  };

  const continueAfterPlan = (plan: SubscriptionPlan) => {
    const canSkipShopSelect = authorizedShops.length === 1 && shopifyConnections.every(shop => shop.authorized);
    if (!canSkipShopSelect) {
      setShopCheckoutError(null);
      setPendingPlan(plan);
      setIsSelecting(false);
      return;
    }

    void startShopifyCheckout(plan, soleAuthorizedShop?.id);
  };

  const handlePlanSelect = async (plan: SubscriptionPlan) => {
    setIsSelecting(true);
    setSelectError(null);

    // Shopify-billed companies approve the charge on Shopify — there is no Stripe
    // checkout or payment-method step. Start an AppSubscription (a plan change creates
    // a new one; Shopify cancels the old on approval) and redirect.
    if (currentSubscription?.provider === 'shopify') {
      if (!connectionsLoaded) {
        setPendingPlan(plan);
        setIsSelecting(false);
        return;
      }
      continueAfterPlan(plan);
      return;
    }

    if (products?.status !== 'success') {
      setIsSelecting(false);
      return;
    }

    const newLimit = plan.features?.find(f => f.featureKey === 'max_products')?.limitValue || 0;

    const isWithinLimit = newLimit === -1 || products?.data?.filter(p => p.active).length <= newLimit;

    if (isWithinLimit && currentSubscription?.status === 'canceled') {
      redirectToCheckout(plan.id, plan.name, plan.baseFeeInCents);
    } else {
      navigate(`/subscription/details?planId=${plan.id}`);
    }

    setIsSelecting(false);
  };

  const isCurrentPlan = (plan: SubscriptionPlan) => {
    return currentSubscription?.planId === plan.id && currentSubscription.status === 'active';
  };

  const handleClose = () => {
    if (!onClose) return;
    setPendingPlan(null);
    setShopCheckoutError(null);
    setSelectError(null);
    onClose();
  };
  

  return (
    <>
      <Dialog
        open={planDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        disableEscapeKeyDown={!onClose || shopDialogOpen}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={600}>
              Select a Plan
            </Typography>
            {onClose && (
              <IconButton onClick={handleClose} size="small">
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogHeader>

        <DialogContent sx={{ p: 3 }}>
          {selectError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {selectError}
            </Alert>
          )}
          {isLoadingPlans ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : plansError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {plansError instanceof Error ? plansError.message : String(plansError)}
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {plans
                ?.filter(plan => plan.isActive)
                .sort((a, b) => a.baseFeeInCents - b.baseFeeInCents)
                .map((plan) => (
                  <Grid item xs={12} sm={6} md={3} key={plan.id}>
                    <SubscriptionCard
                      plan={plan}
                      isCurrentPlan={isCurrentPlan(plan)}
                      onSelect={handlePlanSelect}
                      disabled={isSelecting}
                    />
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <SelectShopifyShopDialog
        open={shopDialogOpen}
        plan={pendingPlan}
        previouslyBilledShopDomain={currentSubscription?.shopifyShopDomain}
        checkoutError={shopCheckoutError}
        onClose={() => {
          setPendingPlan(null);
          setShopCheckoutError(null);
          setIsSelecting(false);
        }}
      />
    </>
  );
};
